// packages/api/src/PublicAccountOrderService.ts
//
// ROADMAP #204 / #281 — the PUBLIC account-order rail: a gift to a cause, or a membership, bought on an organizer page —
// or (#204 step 4) a gift on an EVENT's website, from its Donate block (placed by the event's `url`).
// start (the order + Stripe's PaymentIntent) → the card form in the browser → finalize (the server asks Stripe and, on a
// paid intent, records the gift and issues its receipt). The Stripe webhook is the safety net for a tab that died.
// eventsmagic-api: src/app/graphql/types/publicAccountOrderTypes.ts, src/core/PublicAccountOrderCore.ts.
//
// ⚠ Notice what a line does NOT carry: no currency, no fee. A gift's amount is the donor's own choice — the server checks
//   it against the cause (its floor, its amounts) and reads everything else from the database. A MEMBERSHIP line carries
//   no amount at all: it is sold at its type's price (any amount sent is ignored).
// ⚠ ONE KIND PER ORDER — a gift and a membership are never mixed (the server refuses it on `lines`).
// ⚠ Refusals are FIELD-KEYED: `line:<index>` (the amount / the cause), `lines`, `buyerName`, `buyerEmail`,
//   `buyerStreetAddress`, `wantsReceipt`, `organizerSlug`, `url`.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { errorMessages } from "./opResultHelpers";

/** The kinds of line one account order holds (mirror of the API's ACCOUNT_ORDER_LINE_KIND). */
export const ACCOUNT_ORDER_LINE_KIND = {
  DONATION: "donation",
  /** #281 — a membership type, at the TYPE's price. */
  MEMBERSHIP: "membership",
} as const;

/** The order statuses (mirror of the API's ACCOUNT_ORDER_STATUS). */
export const ACCOUNT_ORDER_STATUS = {
  PENDING_PAYMENT: "pending_payment",
  PAID: "paid",
  CANCELED: "canceled",
  FAILED: "failed",
} as const;

/** #204 step 6a — how often a recurring gift is charged (mirror of the API's `recurring` input). */
export const RECURRING_INTERVAL = {
  MONTH: "month",
} as const;

/** A gift: the cause and the donor's amount. */
export interface PublicAccountOrderDonationLineInput {
  lineKind: typeof ACCOUNT_ORDER_LINE_KIND.DONATION;
  causeId: string;
  amount: number;
}

/** #281 — a membership: the type only (its price is the server's). */
export interface PublicAccountOrderMembershipLineInput {
  lineKind: typeof ACCOUNT_ORDER_LINE_KIND.MEMBERSHIP;
  membershipTypeId: string;
}

export type PublicAccountOrderLineInput = PublicAccountOrderDonationLineInput | PublicAccountOrderMembershipLineInput;

export interface PublicAccountOrderStartInput {
  /** The browser placing it — only it may confirm the payment. ⚠ A BINDING write: mint it (`ensureVisitorId`). */
  visitorId: string;
  /**
   * WHERE it is placed — ONE of the two: an organizer page (its slug), or an event's website (#204 step 4 — the event's
   * full public URL, as every other event rail sends it; the cause must then be one of the event's causes, and the event's
   * test mode decides test cards).
   */
  organizerSlug?: string | null;
  url?: string | null;
  lines: PublicAccountOrderLineInput[];
  /** A re-started checkout (the amount changed after a card form opened): this browser's previous unpaid order. */
  replaceOrderId?: string | null;
  /**
   * #204 step 6a — 'month': a MONTHLY gift (exactly one donation line; a membership or a second line is refused on
   * `lines`), charged every month until the donor stops it. ⚠ Leave it OUT for a one-time gift — never send null: an
   * older gateway refuses an input field it does not know, and a one-time gift must not depend on this deploy.
   */
  recurring?: typeof RECURRING_INTERVAL.MONTH;
  buyerName: string;
  buyerEmail: string;
  buyerPhone?: string | null;
  buyerStreetAddress?: string | null;
  buyerExtraAddress?: string | null;
  buyerCity?: string | null;
  buyerStateProvince?: string | null;
  buyerPostalCode?: string | null;
  buyerCountry?: string | null;
  /** The donor asks for a tax receipt (a cause that gives official receipts). */
  wantsReceipt?: boolean | null;
  /** Not named among the donors (the receipt still names the donor). */
  isAnonymous?: boolean | null;
  /** A dedication or a note to the host. */
  message?: string | null;
  /** #204 — the donor agrees to show the note with the gift on a public supporter list. */
  messageIsPublic?: boolean | null;
}

/** #204 — a gift this browser made on an event, as My orders shows it. */
export interface PublicMyGift {
  causeName?: string | null;
  amount?: number | null;
  currencyCode?: string | null;
  receivedAt?: string | null;
  donationNumber?: string | null;
  /** received | refunded */
  donationStatus?: string | null;
  /** A month of a monthly gift. */
  isMonthly?: boolean | null;
  isTest?: boolean | null;
}

const PUBLIC_ACCOUNT_ORDER_GIFTS_MINE_QUERY = gql`
  query PublicAccountOrderGiftsMine($params: PublicAccountOrderGiftsMineInput) {
    publicAccountOrderGiftsMine(params: $params) {
      code
      errors { name errors }
      data { causeName amount currencyCode receivedAt donationNumber donationStatus isMonthly isTest }
    }
  }
`;

/** What Stripe.js needs to show the card form, and what the donor is charged (the SERVER's numbers). */
export interface PublicAccountOrderPayment {
  orderId: string;
  referenceCode?: string | null;
  clientSecret: string;
  publishableKey: string;
  /** The host's connected account (a direct charge) — null in test mode. */
  connectedAccountId: string | null;
  currencyCode?: string | null;
  subtotal: number;
  serviceFeeToGuest?: number | null;
  processorFeeToGuest?: number | null;
  total: number;
  isTest?: boolean | null;
  /** #204 step 6a — the monthly gift a monthly sign-up made (null / absent for a one-time order). */
  recurringGiftId?: string | null;
}

export interface PublicAccountOrder {
  orderId: string;
  /** pending_payment | paid | canceled | failed */
  orderStatus?: string | null;
  referenceCode?: string | null;
  currencyCode?: string | null;
  total?: number | null;
  isTest?: boolean | null;
  /** The receipts issued for its gifts (TEST-… in test mode). */
  receiptNumbers?: (string | null)[] | null;
  /** #281 — the memberships it issued or renewed. EMPTY for a test purchase, and until the webhook settles the order. */
  membershipNumbers?: (string | null)[] | null;
  /** #204 step 6a — the first month of a MONTHLY gift (the thank-you says "every month"). Absent on an older gateway. */
  monthly?: boolean | null;
  /** Stripe is still processing — the gift is recorded when it finishes (the webhook). */
  processing?: boolean | null;
}

/**
 * #204 step 6a — `recurringGiftId` is asked for ONLY by a monthly start: a one-time gift must keep working on a gateway
 * that has not been recomposed with monthly giving (a field it does not know refuses the WHOLE mutation).
 */
const publicAccountOrderStartMutation = (monthly: boolean) => gql`
  mutation PublicAccountOrderStart($params: PublicAccountOrderStartInput) {
    publicAccountOrderStart(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        orderId
        referenceCode
        clientSecret
        publishableKey
        connectedAccountId
        currencyCode
        subtotal
        serviceFeeToGuest
        processorFeeToGuest
        total
        isTest
        ${monthly ? "recurringGiftId" : ""}
      }
    }
  }
`;

const PUBLIC_ACCOUNT_ORDER_START_MUTATION = publicAccountOrderStartMutation(false);
const PUBLIC_ACCOUNT_ORDER_START_MONTHLY_MUTATION = publicAccountOrderStartMutation(true);

const publicAccountOrderFinalizeMutation = (withNewFields: boolean) => gql`
  mutation PublicAccountOrderFinalize($params: PublicAccountOrderInput) {
    publicAccountOrderFinalize(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        orderId
        orderStatus
        referenceCode
        currencyCode
        total
        isTest
        receiptNumbers
        ${withNewFields ? "membershipNumbers" : ""}
        ${withNewFields ? "monthly" : ""}
        processing
      }
    }
  }
`;

/**
 * #281 — ⚠ A NEW FIELD ON THE FINALIZE ANSWER IS A DEPLOY-ORDER HAZARD: a gateway not yet recomposed refuses the WHOLE
 * mutation, and a refused finalize reads as "pending" — every GIFT would end on "still being confirmed". So it asks with
 * the newer fields (`membershipNumbers` #281, `monthly` #204 step 6a) and, only when the gateway does not know one of
 * them, asks again without them (finalize is idempotent). Without `monthly` a monthly gift's thank-you falls back to
 * what the start answered (`recurringGiftId`).
 */
const PUBLIC_ACCOUNT_ORDER_FINALIZE_MUTATION = publicAccountOrderFinalizeMutation(true);
const PUBLIC_ACCOUNT_ORDER_FINALIZE_MUTATION_BASE = publicAccountOrderFinalizeMutation(false);

/** #281 / #204 step 6a — the gateway refused the finalize because it does not know a newer answer field yet (not recomposed) — nothing else. */
export const refusedUnknownFinalizeFields = (result: OpResult): boolean =>
  !result.didSucceed() && /Cannot query field ["'](membershipNumbers|monthly)["'] on type ["']PublicAccountOrder["']/.test(errorMessages(result).join("\n"));

export class PublicAccountOrderService {
  /**
   * Start a gift or a membership (BROWSER, through the same-origin proxy): the order and its card form. Nothing is charged
   * yet. #204 step 6a — with `recurring: 'month'` it is a MONTHLY gift: `clientSecret` is the first invoice's, confirmed by
   * the SAME card form, and the answer names the `recurringGiftId`.
   */
  static async start(input: PublicAccountOrderStartInput, options: Record<string, any> = {}): Promise<OpResult> {
    const mutation = input.recurring ? PUBLIC_ACCOUNT_ORDER_START_MONTHLY_MUTATION : PUBLIC_ACCOUNT_ORDER_START_MUTATION;

    return ApiService.executeMutation(mutation, { params: input }, options);
  }

  /** #204 — the gifts this browser made on this event, newest first (My orders). A first-time browser reads nothing. */
  static async giftsMine(url: string, visitorId: string | null, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(PUBLIC_ACCOUNT_ORDER_GIFTS_MINE_QUERY, { params: { url, visitorId } }, { fetchPolicy: "no-cache", ...options });
  }

  /** The card form succeeded in the browser: the server asks Stripe and, on a paid intent, records the gift. Idempotent. */
  static async finalize(input: { visitorId: string; orderId: string }, options: Record<string, any> = {}): Promise<OpResult> {
    const result = await ApiService.executeMutation(PUBLIC_ACCOUNT_ORDER_FINALIZE_MUTATION, { params: input }, options);

    return refusedUnknownFinalizeFields(result)
      ? ApiService.executeMutation(PUBLIC_ACCOUNT_ORDER_FINALIZE_MUTATION_BASE, { params: input }, options)
      : result;
  }
}
